import type { QueryClient } from '@tanstack/react-query';
import type { PromotionTarget, QueueFilter } from '@/types/api';

/* ---------------------------------------------------------------------------
   Query keys, and the cross-entity invalidation graph.

   This is the part generated API hooks bury and that actually matters here:
   almost nothing on this surface is a self-contained resource.

     * Claiming a chat changes the queue row, the queue's `mine` and `unclaimed`
       filters, and the chat's own composer predicate.
     * Posting a message starts a run, which changes the composer, the promotion
       gate (a run in flight blocks a promotion) and, once it pushes, the diff.
     * A promotion changes the chat status, the deploys, the gate, the diff
       against BOTH targets, and the queue row.

   Keeping the fan-out written down in one place is what stops the Push to
   Production button staying enabled after a run started underneath it.
--------------------------------------------------------------------------- */

export const qk = {
  me: ['expert', 'me'] as const,

  projects: ['expert', 'projects'] as const,

  queue: (filter: QueueFilter, page: number) => ['expert', 'queue', filter, page] as const,
  queueAll: ['expert', 'queue'] as const,

  chat: (chatId: number) => ['expert', 'chat', chatId] as const,
  diff: (chatId: number, target: PromotionTarget, repoId?: number | null) =>
    ['expert', 'chat', chatId, 'diff', target, repoId ?? 'all'] as const,
  diffAll: (chatId: number) => ['expert', 'chat', chatId, 'diff'] as const,
  preflight: (chatId: number, target: PromotionTarget) => ['expert', 'chat', chatId, 'preflight', target] as const,
  preflightAll: (chatId: number) => ['expert', 'chat', chatId, 'preflight'] as const,
};

const invalidate = (qc: QueryClient, key: readonly unknown[]) => qc.invalidateQueries({ queryKey: key as unknown[] });

/** A claim was taken, refreshed or released. The queue's filters all move. */
export function afterClaimChange(qc: QueryClient, chatId: number) {
  void invalidate(qc, qk.chat(chatId));
  void invalidate(qc, qk.queueAll);
  void invalidate(qc, qk.projects);
}

/**
 * A run was accepted. The chat document moves for reasons the stream does not
 * carry — the composer predicate and the promotion gate both flip on
 * `run_in_progress` — and once the run pushes, the diff is stale.
 */
export function afterRunAccepted(qc: QueryClient, chatId: number) {
  void invalidate(qc, qk.chat(chatId));
  void invalidate(qc, qk.preflightAll(chatId));
}

/** A run ended. Everything it could have touched is now suspect. */
export function afterRunSettled(qc: QueryClient, chatId: number) {
  void invalidate(qc, qk.chat(chatId));
  void invalidate(qc, qk.diffAll(chatId));
  void invalidate(qc, qk.preflightAll(chatId));
  void invalidate(qc, qk.queueAll);
}

/**
 * A promotion started, advanced or ended.
 *
 * The diff is invalidated for BOTH targets, not just the one promoted: merging
 * into staging changes what a production diff would show, and an expert who
 * flips the target selector after a staging promotion must not be handed the
 * pre-merge answer out of cache.
 */
export function afterPromotionChange(qc: QueryClient, chatId: number) {
  void invalidate(qc, qk.chat(chatId));
  void invalidate(qc, qk.diffAll(chatId));
  void invalidate(qc, qk.preflightAll(chatId));
  void invalidate(qc, qk.queueAll);
  void invalidate(qc, qk.projects);
}

/** The thread was handed back to the client. It leaves the queue entirely. */
export function afterRequestChanges(qc: QueryClient, chatId: number) {
  void invalidate(qc, qk.chat(chatId));
  void invalidate(qc, qk.queueAll);
  void invalidate(qc, qk.projects);
}
